import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useToast } from '../../../hooks/toast';
import {
  IClient,
  IDocument,
  IPrescriptionItem,
  IModelDocument,
  IProfessional,
} from '../../../shared/interfaces';
import api from '../../../services/api';
import {
  AButtonPrint,
  AContainerFooter,
  ASelect,
  ContainerForm,
  Loading,
  OptionFooter,
  PageIntro,
  PageLayout,
  PagePanel,
} from '../../../components';
import {
  CopyOutlined,
  DeleteOutlined,
  FileAddOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { Button, Col, Form, Input, Row, Space, Tooltip } from 'antd';
import ReactQuill from 'react-quill';
import {
  formatsText,
  modulesText,
} from '../../../shared/utils/propreties-text';
import { schemaClientDocument } from '../schemas/yup.client-document.schema';
import { getSelectClients, getSelectProfessionals } from '../../../http';
import * as S from './styles';

const defaultPrescriptionTemplate =
  '<p><strong>Paciente:</strong> {nome}</p><p><strong>Data:</strong> {dataReceita}</p><p>{itensReceita}</p><p>{orientacoesReceita}</p>';

const createEmptyPrescriptionItem = (): IPrescriptionItem => ({
  medicine: '',
  dosage: '',
  frequency: '',
  duration: '',
  instructions: '',
});

const DocumentCreateEdit: React.FC = () => {
  const { id } = useParams();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [professionals, setProfessionals] = useState<IProfessional[]>([]);
  const [clients, setClients] = useState<IClient[]>([]);
  const [professionalId, setProfessionalId] = useState<string>();
  const [clientId, setClientId] = useState<string>();
  const [modelDocumentId, setModelDocumentId] = useState<string>();
  const [modelsDocuments, setModelsDocuments] = useState<IModelDocument[]>([]);
  const [text, setText] = useState<string>();
  const [loading, setLoading] = useState<boolean>(true);
  const [showButtonPrint, setShowButtonPrint] = useState<boolean>(false);
  const isPrescriptionRoute = location.pathname.startsWith('/prescription');
  const [kind, setKind] = useState<'GENERAL' | 'PRESCRIPTION'>(
    isPrescriptionRoute || searchParams.get('kind') === 'PRESCRIPTION'
      ? 'PRESCRIPTION'
      : 'GENERAL',
  );
  const [prescriptionItems, setPrescriptionItems] = useState<
    IPrescriptionItem[]
  >([createEmptyPrescriptionItem()]);
  const [prescriptionGuidelines, setPrescriptionGuidelines] =
    useState<string>('');
  const [prescriptionDate, setPrescriptionDate] = useState<string>('');

  const isPrescription = kind === 'PRESCRIPTION';

  const getListRoute = (value?: 'GENERAL' | 'PRESCRIPTION') =>
    value === 'PRESCRIPTION' ? '/prescriptions' : '/documents';

  const getPrintRoute = (value?: 'GENERAL' | 'PRESCRIPTION') =>
    value === 'PRESCRIPTION'
      ? `/prescription/${id}/print`
      : `/document/${id}/print`;

  const keywords = [
    { key: '{nome}', description: 'Nome do cliente' },
    { key: '{cpf}', description: 'CPF do cliente' },
    { key: '{nascimento}', description: 'Data de nascimento do cliente' },
    { key: '{celular}', description: 'Nº celular do cliente' },
    { key: '{sexo}', description: 'Sexo do cliente' },
    { key: '{estadoCivil}', description: 'Estado civil do cliente' },
    { key: '{email}', description: 'E-mail do cliente' },
    { key: '{nacionalidade}', description: 'Nacionalidade do cliente' },
    { key: '{naturalidade}', description: 'Naturalidade do cliente' },
    { key: '{profissao}', description: 'Profissão do cliente' },
    { key: '{cep}', description: 'CEP do endereço do cliente' },
    { key: '{logradouro}', description: 'Logradouro do endereço do cliente' },
    { key: '{numero}', description: 'Número do endereço do cliente' },
    { key: '{bairro}', description: 'Bairro do endereço do cliente' },
    {
      key: '{complemento}',
      description: 'Complemento do endereço do cliente',
    },
    { key: '{cidade}', description: 'Cidade do endereço do cliente' },
    { key: '{uf}', description: 'Estado do endereço do cliente' },
  ];

  const keywordsList = useMemo(
    () => [
      ...keywords,
      ...(isPrescription
        ? [
            {
              key: '{dataReceita}',
              description: 'Data de emissão do receituário',
            },
            {
              key: '{itensReceita}',
              description: 'Lista dos medicamentos prescritos',
            },
            {
              key: '{orientacoesReceita}',
              description: 'Orientações complementares do receituário',
            },
          ]
        : []),
    ],
    [isPrescription],
  );

  const syncKindInQuery = useCallback(
    (nextKind: 'GENERAL' | 'PRESCRIPTION') => {
      const nextParams = new URLSearchParams(searchParams);

      if (nextKind === 'PRESCRIPTION') {
        nextParams.set('kind', 'PRESCRIPTION');
      } else {
        nextParams.delete('kind');
      }

      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const serializePrescriptionItems = useCallback(() => {
    const filteredItems = prescriptionItems.filter(
      (item) =>
        item.medicine?.trim() ||
        item.dosage?.trim() ||
        item.frequency?.trim() ||
        item.duration?.trim() ||
        item.instructions?.trim(),
    );

    return JSON.stringify(filteredItems);
  }, [prescriptionItems]);

  const updatePrescriptionItem = useCallback(
    (index: number, field: keyof IPrescriptionItem, value: string) => {
      setPrescriptionItems((current) =>
        current.map((item, currentIndex) =>
          currentIndex === index ? { ...item, [field]: value } : item,
        ),
      );
    },
    [],
  );

  const loadProfessionals = useCallback(async () => {
    try {
      const data = await getSelectProfessionals();
      setProfessionals(data);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Erro ao listar os profissionais',
      });
    }
  }, [addToast]);

  const loadModelsDocuments = useCallback(async () => {
    try {
      const response = await api.get(`/documents/select?kind=${kind}`);
      setModelsDocuments(response.data);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Erro ao listar os profissionais',
      });
    } finally {
      setLoading(false);
    }
  }, [addToast, kind]);

  const loadClients = useCallback(async () => {
    await getSelectClients(1)
      .then((data) => setClients(data))
      .catch((error) => {
        addToast({
          type: 'error',
          title: 'Erro',
          description: `${
            error.response.data.error ?? 'Erro ao listar os clientes'
          }`,
        });
      });
  }, [addToast]);

  const loadData = useCallback(async () => {
    interface IResponseDocument {
      data: IDocument;
    }

    await api
      .get(`/clients-documents/${id}`)
      .then((response) => {
        const { data }: IResponseDocument = response;
        setModelDocumentId(data.document_id);
        setClientId(data.client_id);
        setProfessionalId(data.professional_id);
        setText(data.text_description);
        setKind(data.kind || data.model_kind || 'GENERAL');
        setPrescriptionGuidelines(data.prescription_guidelines || '');
        setPrescriptionDate(
          data.prescription_date ? data.prescription_date.slice(0, 10) : '',
        );
        try {
          const parsedItems = Array.isArray(data.prescription_items)
            ? data.prescription_items
            : JSON.parse(data.prescription_items || '[]');
          setPrescriptionItems(
            parsedItems.length ? parsedItems : [createEmptyPrescriptionItem()],
          );
        } catch (_error) {
          setPrescriptionItems([createEmptyPrescriptionItem()]);
        }
        setShowButtonPrint(true);
      })
      .catch((error) => {
        addToast({
          type: 'error',
          title: 'Erro',
          description:
            error.response?.data?.message ?? 'Erro ao carregar documento',
        });
      });
  }, [addToast, id]);

  const handleCreateSubmit = useCallback(async () => {
    setLoading(true);

    if (
      isPrescription &&
      prescriptionItems.every((item) => !item.medicine?.trim())
    ) {
      addToast({
        type: 'error',
        title: 'Medicamento obrigatório',
        description:
          'Informe pelo menos um medicamento para emitir o receituário.',
      });
      setLoading(false);
      return;
    }

    schemaClientDocument
      .validate({
        client_id: clientId,
        professional_id: professionalId,
        document_id: modelDocumentId,
        text_description: text,
      })
      .then(async (validated) => {
        const payload = {
          ...validated,
          kind,
          prescription_items: isPrescription
            ? serializePrescriptionItems()
            : undefined,
          prescription_guidelines: isPrescription
            ? prescriptionGuidelines
            : undefined,
          prescription_date: isPrescription
            ? prescriptionDate || undefined
            : undefined,
        };

        await api
          .post(`/clients-documents/`, payload)
          .then(() => {
            addToast({
              type: 'success',
              title: 'Cadastrado',
              description: `${
                isPrescription ? 'Receituário' : 'Documento'
              } cadastrado com sucesso.`,
            });
            setTimeout(function () {
              navigate(getListRoute(kind));
            }, 1000);
          })
          .catch((err) => {
            const message = err.response.data.message;
            addToast({
              type: 'error',
              title: 'Erro',
              description: message ?? 'Erro ao cadastrar',
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: 'Campos em branco',
          description: err.errors[0],
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    addToast,
    clientId,
    isPrescription,
    kind,
    modelDocumentId,
    navigate,
    professionalId,
    prescriptionDate,
    prescriptionGuidelines,
    prescriptionItems,
    serializePrescriptionItems,
    text,
  ]);

  const handleEditSubmit = useCallback(async () => {
    setLoading(true);

    if (
      isPrescription &&
      prescriptionItems.every((item) => !item.medicine?.trim())
    ) {
      addToast({
        type: 'error',
        title: 'Medicamento obrigatório',
        description:
          'Informe pelo menos um medicamento para emitir o receituário.',
      });
      setLoading(false);
      return;
    }

    schemaClientDocument
      .validate({
        client_id: clientId,
        professional_id: professionalId,
        document_id: modelDocumentId,
        text_description: text,
      })
      .then(async (validated) => {
        const payload = {
          ...validated,
          kind,
          prescription_items: isPrescription
            ? serializePrescriptionItems()
            : null,
          prescription_guidelines: isPrescription
            ? prescriptionGuidelines
            : null,
          prescription_date: isPrescription ? prescriptionDate || null : null,
        };

        await api
          .patch(`/clients-documents/${id}`, payload)
          .then(() => {
            addToast({
              type: 'info',
              title: 'Alterado',
              description: `${
                isPrescription ? 'Receituário' : 'Documento'
              } alterado com sucesso.`,
            });
          })
          .catch((err) => {
            const message = err.response.data.message;
            addToast({
              type: 'error',
              title: 'Erro',
              description: message ?? 'Erro ao Editar',
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: 'Campos em branco',
          description: err.errors[0],
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    addToast,
    clientId,
    id,
    isPrescription,
    kind,
    modelDocumentId,
    prescriptionDate,
    prescriptionGuidelines,
    prescriptionItems,
    professionalId,
    serializePrescriptionItems,
    text,
  ]);

  const changeText = (selectedId: string) => {
    const selectedModel = modelsDocuments.find((x) => x.id === selectedId);
    setText(selectedModel?.text_description ?? '');
    setModelDocumentId(selectedId);

    if (selectedModel?.kind && selectedModel.kind !== kind) {
      setKind(selectedModel.kind);
      syncKindInQuery(selectedModel.kind);
    }
  };

  const handleCopyKeyword = (keyword: string) => {
    navigator.clipboard.writeText(keyword);
    addToast({
      type: 'success',
      title: 'Copiado',
      description: `Palavra-chave "${keyword}" copiada para a área de transferência`,
    });
  };

  useEffect(() => {
    loadProfessionals();
    loadClients();
    loadModelsDocuments();

    if (id !== 'new') {
      loadData();
    }
  }, [id, loadClients, loadData, loadModelsDocuments, loadProfessionals]);

  useEffect(() => {
    syncKindInQuery(kind);

    if (id === 'new' && isPrescription && !text) {
      setText(defaultPrescriptionTemplate);
    }

    if (isPrescription && prescriptionItems.length === 0) {
      setPrescriptionItems([createEmptyPrescriptionItem()]);
    }
  }, [
    id,
    isPrescription,
    kind,
    prescriptionItems.length,
    syncKindInQuery,
    text,
  ]);

  if (loading) return <Loading load />;

  return (
    <PageLayout>
      <PageIntro
        badge={isPrescription ? 'Receituários' : 'Documentos'}
        icon={<FileAddOutlined />}
        title={`${id === 'new' ? 'Cadastrar' : 'Editar'} ${
          isPrescription ? 'receituário' : 'documento'
        }`}
        description={
          isPrescription
            ? 'Selecione modelo, paciente e profissional para emitir um receituário eletrônico com itens, orientações e assinatura digital.'
            : 'Selecione modelo, cliente e profissional para montar o documento em uma tela alinhada ao padrão visual interno.'
        }
      />
      <PagePanel>
        <ContainerForm>
          <Form layout="vertical" style={{ width: '90%', marginTop: 20 }}>
            <S.StyledCard>
              <S.FormSectionTitle>
                <span className="icon">👤</span>
                Selecionar Modelo e Pessoas
              </S.FormSectionTitle>

              <S.InstructionBox>
                <InfoCircleOutlined className="icon" />
                <p className="text">
                  Selecione o modelo de documento, o cliente e o profissional
                  responsável. O modelo carregará automaticamente o texto do
                  documento.
                </p>
              </S.InstructionBox>

              <Row gutter={16}>
                <Col span={6}>
                  <Form.Item label="Tipo" required>
                    <ASelect
                      value={kind}
                      onChange={(value) => {
                        const nextKind = value as 'GENERAL' | 'PRESCRIPTION';
                        setKind(nextKind);
                        setModelDocumentId(undefined);
                        setModelsDocuments([]);

                        if (nextKind === 'PRESCRIPTION' && !text) {
                          setText(defaultPrescriptionTemplate);
                        }

                        navigate(
                          nextKind === 'PRESCRIPTION'
                            ? `/prescription/${id}`
                            : `/document/${id}`,
                          { replace: true },
                        );
                      }}
                      options={[
                        { label: 'Documento', value: 'GENERAL' },
                        {
                          label: 'Receituário eletrônico',
                          value: 'PRESCRIPTION',
                        },
                      ]}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    label={
                      isPrescription
                        ? 'Modelo de Receituário'
                        : 'Modelo de Documento'
                    }
                    required
                    tooltip="Selecione o template do documento"
                  >
                    <ASelect
                      showSearch
                      placeholder="Modelo de Documento"
                      value={modelDocumentId}
                      onChange={(e) => changeText(e)}
                      filterOption={(input, option: any) =>
                        (option?.label ?? '')
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      allowClear
                      options={modelsDocuments.map((md) => ({
                        label: md.title,
                        value: md.id,
                      }))}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    label="Cliente"
                    required
                    tooltip="Selecione o cliente responsável"
                  >
                    <ASelect
                      showSearch
                      placeholder="Cliente"
                      value={clientId}
                      onChange={(e) => setClientId(e)}
                      filterOption={(input, option: any) =>
                        (option?.label ?? '')
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      allowClear
                      options={clients.map((cli) => ({
                        label: cli.corporate_name,
                        value: cli.id,
                      }))}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    label="Profissional"
                    required
                    tooltip="Selecione o profissional responsável"
                  >
                    <ASelect
                      showSearch
                      placeholder="Profissional"
                      value={professionalId}
                      onChange={(e) => setProfessionalId(e)}
                      filterOption={(input, option: any) =>
                        (option?.label ?? '')
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      allowClear
                      options={professionals.map((prof) => ({
                        label: prof.name,
                        value: prof.id,
                      }))}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </S.StyledCard>

            <S.StyledCard>
              <S.FormSectionTitle>
                <span className="icon">🔑</span>
                Palavras-chave Disponíveis
              </S.FormSectionTitle>

              <S.InstructionBox>
                <InfoCircleOutlined className="icon" />
                <p className="text">
                  Copie e cole as palavras-chave no texto do documento. Elas
                  serão automaticamente substituídas pelos dados do cliente
                  quando o documento for gerado.
                </p>
              </S.InstructionBox>

              <Space direction="vertical" style={{ width: '100%' }} size={0}>
                {keywordsList.map((item, index) => (
                  <S.KeywordItem key={`keyword-${index}`}>
                    <span className="keyword-key">{item.key}</span>
                    <span className="keyword-desc">{item.description}</span>
                    <Tooltip title="Copiar palavra-chave">
                      <Button
                        type="text"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => handleCopyKeyword(item.key)}
                        className="copy-btn"
                      />
                    </Tooltip>
                  </S.KeywordItem>
                ))}
              </Space>

              <div
                style={{
                  marginTop: 16,
                  padding: '12px 12px',
                  backgroundColor: '#fffbe6',
                  borderLeft: '3px solid #faad14',
                  borderRadius: '4px',
                }}
              >
                <strong style={{ color: '#ad6800' }}>⚠️ Importante:</strong>
                <p
                  style={{
                    marginBottom: 0,
                    marginTop: 4,
                    color: '#ad6800',
                    fontSize: 13,
                  }}
                >
                  As palavras-chave devem ser digitadas exatamente como mostrado
                  acima (com as chaves{' '}
                  <code
                    style={{
                      backgroundColor: '#fff7e6',
                      padding: '2px 4px',
                      borderRadius: '2px',
                    }}
                  >
                    {'{'} {'}'}
                  </code>{' '}
                  e sem espaços).
                </p>
              </div>
            </S.StyledCard>

            {isPrescription && (
              <S.StyledCard>
                <S.FormSectionTitle>
                  <span className="icon">💊</span>
                  Dados da Prescrição
                </S.FormSectionTitle>

                <S.InstructionBox>
                  <InfoCircleOutlined className="icon" />
                  <p className="text">
                    Preencha os medicamentos e orientações que devem aparecer no
                    receituário. Esses dados também entram no hash assinado
                    digitalmente.
                  </p>
                </S.InstructionBox>

                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item label="Data da Receita">
                      <Input
                        type="date"
                        value={prescriptionDate}
                        onChange={(e) => setPrescriptionDate(e.target.value)}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={16}>
                    <Form.Item label="Orientações Gerais">
                      <Input.TextArea
                        rows={3}
                        value={prescriptionGuidelines}
                        onChange={(e) =>
                          setPrescriptionGuidelines(e.target.value)
                        }
                        placeholder="Ex.: Evitar álcool, manter hidratação e retornar em 7 dias."
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Space direction="vertical" style={{ width: '100%' }} size={16}>
                  {prescriptionItems.map((item, index) => (
                    <div
                      key={`prescription-item-${index}`}
                      style={{
                        padding: 16,
                        border: '1px solid rgba(15, 23, 42, 0.08)',
                        borderRadius: 16,
                        background: 'rgba(255,255,255,0.88)',
                      }}
                    >
                      <Row gutter={16}>
                        <Col span={8}>
                          <Form.Item
                            label={`Medicamento ${index + 1}`}
                            required
                          >
                            <Input
                              value={item.medicine}
                              onChange={(e) =>
                                updatePrescriptionItem(
                                  index,
                                  'medicine',
                                  e.target.value,
                                )
                              }
                              placeholder="Ex.: Amoxicilina"
                            />
                          </Form.Item>
                        </Col>
                        <Col span={4}>
                          <Form.Item label="Dosagem">
                            <Input
                              value={item.dosage}
                              onChange={(e) =>
                                updatePrescriptionItem(
                                  index,
                                  'dosage',
                                  e.target.value,
                                )
                              }
                              placeholder="500mg"
                            />
                          </Form.Item>
                        </Col>
                        <Col span={5}>
                          <Form.Item label="Frequência">
                            <Input
                              value={item.frequency}
                              onChange={(e) =>
                                updatePrescriptionItem(
                                  index,
                                  'frequency',
                                  e.target.value,
                                )
                              }
                              placeholder="8/8h"
                            />
                          </Form.Item>
                        </Col>
                        <Col span={5}>
                          <Form.Item label="Duração">
                            <Input
                              value={item.duration}
                              onChange={(e) =>
                                updatePrescriptionItem(
                                  index,
                                  'duration',
                                  e.target.value,
                                )
                              }
                              placeholder="7 dias"
                            />
                          </Form.Item>
                        </Col>
                        <Col span={2}>
                          <Form.Item label=" ">
                            <Button
                              danger
                              icon={<DeleteOutlined />}
                              disabled={prescriptionItems.length === 1}
                              onClick={() =>
                                setPrescriptionItems((current) =>
                                  current.length === 1
                                    ? current
                                    : current.filter(
                                        (_, currentIndex) =>
                                          currentIndex !== index,
                                      ),
                                )
                              }
                            />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Form.Item label="Instruções do item">
                        <Input.TextArea
                          rows={2}
                          value={item.instructions}
                          onChange={(e) =>
                            updatePrescriptionItem(
                              index,
                              'instructions',
                              e.target.value,
                            )
                          }
                          placeholder="Ex.: Tomar após as refeições."
                        />
                      </Form.Item>
                    </div>
                  ))}
                </Space>

                <Button
                  type="dashed"
                  onClick={() =>
                    setPrescriptionItems((current) => [
                      ...current,
                      createEmptyPrescriptionItem(),
                    ])
                  }
                  style={{ marginTop: 16 }}
                >
                  Adicionar medicamento
                </Button>
              </S.StyledCard>
            )}

            <S.StyledCard>
              <S.FormSectionTitle>
                <span className="icon">📄</span>
                Conteúdo do Documento
              </S.FormSectionTitle>

              <S.InstructionBox>
                <InfoCircleOutlined className="icon" />
                <p className="text">
                  Escreva o conteúdo do documento. Use as palavras-chave acima
                  para inserir dados dinâmicos que serão preenchidos
                  automaticamente.
                </p>
              </S.InstructionBox>

              <Row>
                <Col span={24}>
                  <Form.Item label="Texto do Documento" required>
                    <ReactQuill
                      theme="snow"
                      value={text}
                      onChange={setText}
                      modules={modulesText}
                      formats={formatsText}
                      style={{ height: '35vh', marginBottom: 20 }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </S.StyledCard>

            <AContainerFooter>
              <OptionFooter
                onClickCancel={() => navigate(getListRoute(kind))}
                onClickOk={id !== 'new' ? handleEditSubmit : handleCreateSubmit}
              />
              <AButtonPrint
                show={showButtonPrint}
                onClick={() => {
                  navigate(getPrintRoute(kind));
                }}
              />
            </AContainerFooter>
          </Form>
        </ContainerForm>
      </PagePanel>
    </PageLayout>
  );
};

export default DocumentCreateEdit;
